import type { LoomAuthoringDocument, LoomSceneNode } from "./loomAuthoringModel";
import { loomAssetDefinitionMatchesDocument } from "./loomAuthoritativeDefinition";
import {
  buildLoomExplicitTrimTopology,
  invalidLoomExplicitTrimMeshReason,
  loomExplicitTrimPositions,
  type LoomExplicitTrimParameterLines,
  type LoomExplicitTrimTopology,
} from "./loomExplicitTrimTopology";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import { LOOM_FACE_OWNER_FUSELAGE } from "./loomExactFaceOwnership";

type Vec3 = readonly [number, number, number];
export type { LoomExplicitTrimParameterLines } from "./loomExplicitTrimTopology";

export interface LoomExplicitTrimSurface {
  boundaryEdgeCount: number; bounds: { min: Vec3; max: Vec3 }; buildMs: number;
  faceCornerParameters?: Float32Array; faces: Uint32Array; faceEntityIds: readonly string[];
  kind: "available"; maxSharedSeamGapM: number;
  method: "reflected-pairwise-hybrid-base" | "frozen-topology-jacobian-linearization";
  nonManifoldEdgeCount: number; parameterDeltas: Readonly<Record<string, number>>;
  parameterLines: LoomExplicitTrimParameterLines; positions: Float32Array;
  seamRings: readonly Uint32Array[];
  sourceFixture: "accepted-starboard-pairwise-hybrid";
  topology: "symmetric-explicit-trim-geometry-only";
}

export interface LoomExplicitTrimUnavailable {
  kind: "unavailable";
  reason: string;
}

export type LoomExplicitTrimPreview = LoomExplicitTrimSurface | LoomExplicitTrimUnavailable;
const topologyCache = new WeakMap<LoomNativeHybridAsset, LoomExplicitTrimTopology>();
const trustedAssetCache = new WeakMap<LoomNativeHybridAsset, boolean>();
const PLANE_EPSILON_M = 1e-8;
const MAX_LOCAL_PARAMETER_FRACTION = 0.1;
const MAX_COMBINED_PARAMETER_FRACTION = 0.12;
const SOURCE_TOLERANCE_M = 1e-5;

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= 1e-8 * Math.max(1, Math.abs(left), Math.abs(right));
}

function sameUniqueSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length || new Set(left).size !== left.length || new Set(right).size !== right.length) {
    return false;
  }
  return left.every((value) => right.includes(value));
}

function canonicalNodes(document: LoomAuthoringDocument): {
  panel: Extract<LoomSceneNode, { type: "primitive" }>["definition"] & { primitive: "wing" };
} | null {
  if (document.nodes.length !== 4 || !sameUniqueSet(document.rootNodeIds, ["vehicle"])) return null;
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const wing = nodes.get("wing");
  const fuselage = nodes.get("fuselage");
  const partner = nodes.get("wing-partner");
  const vehicle = nodes.get("vehicle");
  if (wing?.type !== "primitive" || wing.definition.primitive !== "wing"
      || fuselage?.type !== "primitive" || fuselage.definition.primitive !== "fuselage"
      || partner?.type !== "transform" || partner.inputNodeId !== "wing"
      || partner.operation.transform !== "mirror" || partner.operation.axis !== "y"
      || !nearlyEqual(partner.operation.planeOffsetM, 0)
      || vehicle?.type !== "boolean" || vehicle.operation !== "union"
      || !sameUniqueSet(vehicle.inputNodeIds, ["fuselage", "wing", "wing-partner"])) return null;
  if (wing.definition.panels.length !== 1 || wing.definition.tip !== "flat"
      || !nearlyEqual(wing.definition.tipRadiusM, 0)) return null;
  return { panel: wing.definition };
}

function matchesFuselage(asset: LoomNativeHybridAsset, document: LoomAuthoringDocument): boolean {
  const node = document.nodes.find((candidate) => candidate.id === "fuselage");
  if (node?.type !== "primitive" || node.definition.primitive !== "fuselage") return false;
  const expected = asset.header.parameters.fuselageStations;
  return node.definition.stations.length === expected.length && node.definition.stations.every((station, index) => {
    const target = expected[index];
    if (!target || station.kind !== target.kind || !nearlyEqual(station.xM, Number(target.x))
        || !nearlyEqual(station.zCenterM, Number(target.z_center))) return false;
    if (station.kind === "point") return Number(target.width) === 0 && Number(target.height) === 0;
    return nearlyEqual(station.widthM, Number(target.width))
      && nearlyEqual(station.heightM, Number(target.height))
      && nearlyEqual(station.widthExponent, Number(target.m_w))
      && nearlyEqual(station.heightExponent, Number(target.m_h));
  });
}

function hasTrustedAcceptedReceipt(asset: LoomNativeHybridAsset): boolean {
  const cached = trustedAssetCache.get(asset);
  if (cached !== undefined) return cached;
  const { header } = asset;
  const qc = header.surface.qc;
  const finiteBox = qc.bbox.length === 2 && qc.bbox.every((corner) => (
    corner.length === 3 && corner.every(Number.isFinite)
  ));
  const common = header.accepted && header.surface.accepted && header.surface.elementQuality.passed && qc.passed
    && qc.watertight && qc.manifold && qc.winding_consistent
    && qc.boundary_edges === 0 && qc.nonmanifold_edges === 0 && qc.self_intersections === 0
    && qc.vertices === header.surface.vertexCount && qc.faces === header.surface.faceCount
    && Number.isFinite(qc.area) && qc.area > 0
    && typeof qc.volume === "number" && Number.isFinite(qc.volume) && qc.volume > 0 && finiteBox
    && Number.isFinite(header.surface.maxVehicleFieldResidualM)
    && header.surface.maxVehicleFieldResidualM <= SOURCE_TOLERANCE_M;
  const union = asset.acceptedGeometry.unionReceipt;
  const v3Trusted = asset.integrity.status === "verified" && union !== undefined
    && union.header.qc.vertexCount === header.surface.vertexCount
    && union.header.qc.faceCount === header.surface.faceCount
    && union.header.qc.boundaryEdgeCount === 0 && union.header.qc.nonmanifoldEdgeCount === 0
    && union.header.qc.windingConsistent && union.header.qc.positiveVolume
    && union.header.qc.seamZeroGap && union.header.qc.selfIntersectionCount === 0
    && union.header.qc.componentCount === 1 && union.header.qc.canonicalQcPassed
    && union.sharedSeams.length > 0
    && union.sharedSeams.every(({ maxSurfaceGapM }) => (
      Number.isFinite(maxSurfaceGapM) && maxSurfaceGapM >= 0 && maxSurfaceGapM <= SOURCE_TOLERANCE_M
    ));
  const legacyTrusted = header.seam.sourceRingCount === header.seam.meshUniqueRingCount
    && header.seam.sourceRingCount > 2
    && Number.isFinite(header.seam.maxCoordinateGapM)
    && header.seam.maxCoordinateGapM <= SOURCE_TOLERANCE_M
    && Number.isFinite(header.seam.maxWingFieldResidualM)
    && header.seam.maxWingFieldResidualM <= SOURCE_TOLERANCE_M
    && Number.isFinite(header.seam.maxFuselageFieldResidualM)
    && header.seam.maxFuselageFieldResidualM <= SOURCE_TOLERANCE_M
    && asset.seam.wingVertexIndices.length === header.seam.sourceRingCount
    && asset.seam.fuselageVertexIndices.length === header.seam.sourceRingCount;
  const trusted = common && (header.acceptedGeometry.schema === "loom.accepted-mesh/v3"
    ? v3Trusted : legacyTrusted);
  trustedAssetCache.set(asset, trusted);
  return trusted;
}

function canonicalTargets(asset: LoomNativeHybridAsset, document: LoomAuthoringDocument): Record<string, number> | null {
  const graph = canonicalNodes(document);
  if (!graph || !matchesFuselage(asset, document)) return null;
  const panel = graph.panel.panels[0]!;
  const [shiftX = 0, shiftY = 0, shiftZ = 0] = asset.header.parameters.wingShiftM;
  const perimeterCount = 2 * Number(asset.header.resolution.n_section ?? 0) - 1;
  const airfoils = [panel.airfoilRoot, panel.airfoilTip];
  if (!nearlyEqual(panel.yRootM, 0) || !nearlyEqual(panel.sweepLocationRatio, 0.25)
      || !nearlyEqual(panel.twistRootDeg, 0) || !nearlyEqual(panel.twistLocationRatio, 0.25)
      || !nearlyEqual(panel.xOffsetM, shiftX) || !nearlyEqual(panel.zOffsetM, shiftZ)
      || !nearlyEqual(shiftY, 0) || panel.sectionSamples !== perimeterCount
      || panel.chordRootM <= 0 || airfoils.some((airfoil) => (
        airfoil.family !== "naca4" || !nearlyEqual(airfoil.camberRatio, 0)
        || !nearlyEqual(airfoil.camberLocationRatio, 0) || !nearlyEqual(airfoil.thicknessRatio, 0.12)
        || !airfoil.closedTrailingEdge
      ))) return null;
  const rootAirfoil = panel.airfoilRoot;
  const tipAirfoil = panel.airfoilTip;
  if (rootAirfoil.family !== "naca4" || tipAirfoil.family !== "naca4"
      || !nearlyEqual(rootAirfoil.trailingEdgeThicknessRatio, tipAirfoil.trailingEdgeThicknessRatio)) return null;
  return {
    sweep_deg: panel.sweepDeg,
    taper: panel.chordTipM / panel.chordRootM,
    chord_root: panel.chordRootM,
    half_span: panel.yTipM - panel.yRootM,
    twist_tip_deg: panel.twistTipDeg,
    dihedral_deg: panel.dihedralDeg,
    te_thickness: rootAirfoil.trailingEdgeThicknessRatio,
  };
}

function jacobianDelta(displayUnit: string, jacobianUnit: string, delta: number): number | null {
  if (displayUnit === jacobianUnit) return delta;
  if (displayUnit === "deg" && jacobianUnit === "rad") return delta * Math.PI / 180;
  return null;
}

type DeformationResult =
  | { kind: "available"; positions: Float32Array; deltas: Record<string, number> }
  | { kind: "unavailable"; reason: string };

function deformAcceptedHalf(
  asset: LoomNativeHybridAsset,
  targets: Readonly<Record<string, number>>,
): DeformationResult {
  const { header } = asset;
  const deltas = Object.fromEntries(header.parameters.definitions.map((parameter) => (
    [parameter.id, (targets[parameter.id] ?? Number.NaN) - parameter.value]
  )));
  if (Object.values(deltas).every((delta) => nearlyEqual(delta, 0))) {
    return { kind: "available", positions: new Float32Array(asset.surface.positions), deltas };
  }
  const parameterCount = header.jacobian.parameterOrder.length;
  const dynamicCount = header.jacobian.dynamicVertexCount;
  if (!header.parameters.frozenTopology || !header.jacobian.validation.topologyStable
      || !sameUniqueSet(header.jacobian.parameterOrder, Object.keys(targets))
      || !sameUniqueSet(header.parameters.order, header.jacobian.parameterOrder)
      || header.jacobian.shape.length !== 3 || header.jacobian.shape[0] !== dynamicCount
      || header.jacobian.shape[1] !== 3 || header.jacobian.shape[2] !== parameterCount
      || asset.surface.positions.length !== header.surface.vertexCount * 3
      || asset.surface.faces.length !== header.surface.faceCount * 3
      || asset.jacobian.vertexIndices.length !== dynamicCount
      || asset.jacobian.values.length !== dynamicCount * 3 * parameterCount) {
    return { kind: "unavailable", reason: "The accepted explicit-surface Jacobian contract is invalid" };
  }
  const jacobianDeltas = new Float64Array(parameterCount);
  let combinedFractionSquared = 0;
  for (let index = 0; index < parameterCount; index += 1) {
    const id = header.jacobian.parameterOrder[index]!;
    const parameter = header.parameters.definitions.find((candidate) => candidate.id === id);
    const target = targets[id];
    if (!parameter || !Number.isFinite(target) || !Number.isFinite(parameter.value)
        || !Number.isFinite(parameter.minimum) || !Number.isFinite(parameter.maximum)
        || parameter.maximum <= parameter.minimum || target! < parameter.minimum || target! > parameter.maximum) {
      return { kind: "unavailable", reason: "The requested value is outside the accepted parameter range" };
    }
    const delta = target! - parameter.value;
    const converted = jacobianDelta(parameter.displayUnit, parameter.jacobianUnit, delta);
    if (converted === null || !Number.isFinite(converted)) {
      return { kind: "unavailable", reason: "The accepted explicit-surface Jacobian unit contract is invalid" };
    }
    const fraction = Math.abs(delta) / (parameter.maximum - parameter.minimum);
    if (fraction > MAX_LOCAL_PARAMETER_FRACTION + Number.EPSILON) {
      return { kind: "unavailable", reason: "The requested edit is outside the conservative local Jacobian trust region" };
    }
    combinedFractionSquared += fraction * fraction;
    deltas[id] = delta;
    jacobianDeltas[index] = converted;
  }
  if (Math.sqrt(combinedFractionSquared) > MAX_COMBINED_PARAMETER_FRACTION + Number.EPSILON) {
    return { kind: "unavailable", reason: "The combined edit is outside the conservative local Jacobian trust region" };
  }
  const positions = new Float32Array(asset.surface.positions);
  const seen = new Set<number>();
  for (let row = 0; row < dynamicCount; row += 1) {
    const vertex = asset.jacobian.vertexIndices[row]!;
    if (vertex >= header.surface.vertexCount || seen.has(vertex)) {
      return { kind: "unavailable", reason: "The accepted explicit-surface Jacobian vertex map is invalid" };
    }
    seen.add(vertex);
    for (let axis = 0; axis < 3; axis += 1) {
      let displacement = 0;
      for (let parameter = 0; parameter < parameterCount; parameter += 1) {
        const derivative = asset.jacobian.values[(row * 3 + axis) * parameterCount + parameter];
        if (!Number.isFinite(derivative)) {
          return { kind: "unavailable", reason: "The accepted explicit-surface Jacobian contains non-finite values" };
        }
        displacement += derivative! * jacobianDeltas[parameter]!;
      }
      const positionIndex = vertex * 3 + axis;
      positions[positionIndex] = positions[positionIndex]! + displacement;
    }
  }
  return { kind: "available", positions, deltas };
}

function sharedSeamGap(asset: LoomNativeHybridAsset, positions: Float32Array): number {
  if (asset.acceptedGeometry.header.schema === "loom.accepted-mesh/v3") {
    const seams = asset.acceptedGeometry.unionReceipt?.sharedSeams;
    if (!seams?.length) return Infinity;
    return Math.max(...seams.map(({ maxSurfaceGapM }) => maxSurfaceGapM));
  }
  let maximum = 0;
  for (let index = 0; index < asset.seam.wingVertexIndices.length; index += 1) {
    const wing = asset.seam.wingVertexIndices[index]!;
    const fuselage = asset.seam.fuselageVertexIndices[index]!;
    if (wing * 3 + 2 >= positions.length || fuselage * 3 + 2 >= positions.length) return Infinity;
    maximum = Math.max(maximum, Math.hypot(
      positions[wing * 3]! - positions[fuselage * 3]!,
      positions[wing * 3 + 1]! - positions[fuselage * 3 + 1]!,
      positions[wing * 3 + 2]! - positions[fuselage * 3 + 2]!,
    ));
  }
  return maximum;
}

function bounds(positions: Float32Array): { min: Vec3; max: Vec3 } {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (let offset = 0; offset < positions.length; offset += 3) for (let axis = 0; axis < 3; axis += 1) {
    min[axis] = Math.min(min[axis]!, positions[offset + axis]!);
    max[axis] = Math.max(max[axis]!, positions[offset + axis]!);
  }
  return { min: min as unknown as Vec3, max: max as unknown as Vec3 };
}

export function deriveLoomExplicitTrimPreview(
  asset: LoomNativeHybridAsset,
  document: LoomAuthoringDocument,
): LoomExplicitTrimPreview {
  const startedAt = performance.now();
  if (!hasTrustedAcceptedReceipt(asset)) {
    return { kind: "unavailable", reason: "The source explicit hybrid does not carry a trusted accepted geometry receipt" };
  }
  const definitionMatches = loomAssetDefinitionMatchesDocument(asset, document);
  const targets = definitionMatches === null ? canonicalTargets(asset, document) : null;
  if (definitionMatches === false || (definitionMatches === null && !targets)) {
    return { kind: "unavailable", reason: "Pattern does not match the accepted definition binding and topology identity" };
  }
  const deformed: DeformationResult = definitionMatches
    ? { kind: "available", positions: new Float32Array(asset.surface.positions), deltas: {} }
    : deformAcceptedHalf(asset, targets!);
  if (deformed.kind === "unavailable") return deformed;
  let topology = topologyCache.get(asset);
  try {
    if (!topology) {
      topology = buildLoomExplicitTrimTopology(asset, PLANE_EPSILON_M);
      topologyCache.set(asset, topology);
    }
  } catch {
    return { kind: "unavailable", reason: "The accepted explicit surface cannot be clipped into the symmetric Pattern topology" };
  }
  const positions = loomExplicitTrimPositions(topology, deformed.positions);
  const invalidMeshReason = invalidLoomExplicitTrimMeshReason(positions, topology);
  if (invalidMeshReason) return { kind: "unavailable", reason: invalidMeshReason };
  const maxSharedSeamGapM = sharedSeamGap(asset, deformed.positions);
  if (!Number.isFinite(maxSharedSeamGapM) || maxSharedSeamGapM > SOURCE_TOLERANCE_M) {
    return { kind: "unavailable", reason: "The explicit surface no longer has conforming shared seams" };
  }
  return {
    boundaryEdgeCount: topology.boundaryEdgeCount,
    bounds: bounds(positions),
    buildMs: performance.now() - startedAt,
    ...(topology.faceCornerParameters ? { faceCornerParameters: topology.faceCornerParameters } : {}),
    faces: topology.faces,
    faceEntityIds: Array.from(topology.faceOwnerKinds, (owner, face) => (
      owner === LOOM_FACE_OWNER_FUSELAGE ? "fuselage" : topology.faceMirrored[face] ? "wing-partner" : "wing"
    )),
    kind: "available",
    maxSharedSeamGapM,
    method: Object.values(deformed.deltas).every((delta) => nearlyEqual(delta, 0))
      ? "reflected-pairwise-hybrid-base" : "frozen-topology-jacobian-linearization",
    nonManifoldEdgeCount: topology.nonManifoldEdgeCount,
    parameterDeltas: deformed.deltas,
    parameterLines: topology.parameterLines,
    positions,
    seamRings: topology.seamRings,
    sourceFixture: "accepted-starboard-pairwise-hybrid",
    topology: "symmetric-explicit-trim-geometry-only",
  };
}
